import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowDown, Check } from "lucide-react";
import { services } from "@/data/site";
import useHashScroll from "@/lib/useHashScroll";
import { scrollToHash } from "@/lib/scroll";
import { EASE, Eyebrow, Reveal, fadeUp, staggerContainer } from "@/components/site/motion";

const detailServices = [
  {
    ...services[0],
    num: "01",
    overviewDesc: "Custom software, SaaS products, APIs, platforms, and business applications engineered around real requirements.",
    heading: "Build software around the business — not the other way around.",
    copy: "We design and engineer custom software products, SaaS platforms, APIs, portals, and business applications around the workflows, users, and goals they need to support.",
    capabilities: ["Custom Software Development", "SaaS Development", "Web Applications", "Business Applications", "API & Backend Development", "Product Engineering", "System Architecture", "Software Modernization"],
    variant: "software",
  },
  {
    ...services[1],
    num: "02",
    overviewDesc: "Modern web and mobile experiences designed for usability, performance, scalability, and long-term growth.",
    heading: "Digital experiences built for real users.",
    copy: "From customer-facing websites to complex web platforms and mobile applications, we create digital experiences that balance usability, performance, reliability, and technical scalability.",
    capabilities: ["Website Development", "Web Application Development", "Mobile App Development", "E-commerce Development", "Web Portals", "Content Management Systems", "Responsive Interfaces", "UI Implementation"],
    variant: "web",
  },
  {
    ...services[2],
    num: "03",
    overviewDesc: "Intelligent systems, workflow automation, machine learning, and data-driven solutions that reduce manual effort and unlock new capabilities.",
    heading: "Turn repetitive work and disconnected data into intelligent systems.",
    copy: "We help businesses apply AI, automation, and data-driven technology where it can create practical value — from smarter workflows to intelligent product features and operational automation.",
    capabilities: ["AI Integration", "Workflow Automation", "Machine Learning", "Data Analytics", "Intelligent Applications", "Business Process Automation", "AI-Powered Features", "Data Integration"],
    variant: "ai",
  },
  {
    ...services[3],
    num: "04",
    overviewDesc: "Cloud environments, deployments, hosting, infrastructure, and technical foundations built for reliability and scale.",
    heading: "Infrastructure designed to support growth, not slow it down.",
    copy: "We design, deploy, and improve cloud and hosting environments that support modern applications with a focus on reliability, security, performance, and maintainability.",
    capabilities: ["Cloud Infrastructure", "Application Deployment", "Hosting Architecture", "Server Configuration", "CI/CD Pipelines", "Environment Management", "Infrastructure Modernization", "Performance & Reliability"],
    variant: "cloud",
  },
  {
    ...services[4],
    num: "05",
    overviewDesc: "Modernize systems, processes, and operations through thoughtful technology integration and automation.",
    heading: "Modernize how your business operates.",
    copy: "Digital transformation is not simply replacing old software. We help businesses rethink workflows, connect systems, reduce manual processes, and introduce technology where it improves operations.",
    capabilities: ["Process Modernization", "Legacy System Improvement", "System Integration", "Workflow Digitization", "Business Automation", "Platform Migration", "Operational Systems", "Technology Implementation"],
    variant: "transform",
  },
  {
    ...services[5],
    num: "06",
    overviewDesc: "Practical technical guidance for architecture, software strategy, scalability, integrations, testing, and implementation.",
    heading: "Make better technical decisions before complexity grows.",
    copy: "Bytevora provides practical technology guidance across architecture, software strategy, infrastructure, scalability, integrations, testing, and implementation.",
    capabilities: ["Technology Strategy", "Software Architecture", "Technical Planning", "Architecture Review", "System Integration Strategy", "Scalability Planning", "Testing & Quality Assurance", "Technical Support & Maintenance"],
    variant: "consulting",
  },
];

const approach = [
  { title: "Understand First", desc: "We clarify the problem, priorities, users, workflows, and constraints before proposing a solution." },
  { title: "Design for the Future", desc: "Architecture decisions consider maintainability, scalability, performance, and future evolution." },
  { title: "Build in Iterations", desc: "We develop and refine solutions in structured stages rather than treating delivery as a single handoff." },
  { title: "Support What We Build", desc: "Good systems require monitoring, maintenance, improvement, and technical continuity after launch." },
];

const audiences = [
  { title: "Startups", desc: "Build MVPs, SaaS products, mobile applications, and technical foundations designed to evolve beyond the first release." },
  { title: "Growing Businesses", desc: "Automate operations, integrate systems, modernize applications, and improve digital workflows." },
  { title: "Established Companies", desc: "Modernize legacy systems, build internal platforms, introduce AI, and strengthen cloud or software architecture." },
  { title: "Product Teams", desc: "Extend engineering capacity, integrate new capabilities, modernize infrastructure, or solve complex technical problems." },
];

const whyPoints = [
  { title: "Practical Solutions", desc: "Technology should solve the real problem without unnecessary complexity." },
  { title: "Scalable Foundations", desc: "Systems should be designed to support growth and future requirements." },
  { title: "Technology-Neutral Thinking", desc: "We choose technologies based on the product and business need rather than forcing every project into the same stack." },
  { title: "Long-Term Perspective", desc: "We consider what happens after launch — maintenance, reliability, improvement, and scale." },
];

function ServiceVisual({ variant }) {
  const gid = `sv-${variant}`;
  const stroke = `url(#${gid})`;
  return (
    <div className="relative overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-[0_30px_60px_-40px_rgba(11,18,32,0.25)]">
      <div className="bg-grid-light absolute inset-0 opacity-50" aria-hidden="true" />
      <svg viewBox="0 0 480 320" className="relative block h-auto w-full" role="img" aria-label={`Abstract ${variant} diagram`}>
        <defs>
          <linearGradient id={gid} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#2563EB" />
            <stop offset="100%" stopColor="#7C3AED" />
          </linearGradient>
        </defs>

        {variant === "software" && (
          <g>
            <line x1="70" y1="64" x2="70" y2="274" stroke={stroke} strokeWidth="1.4" opacity="0.5" className="dash-flow" />
            {[40, 110, 180, 250].map((y, i) => (
              <g key={y}>
                <rect x="90" y={y} width="300" height="48" rx="12" fill="#fff" stroke={stroke} strokeWidth="1.5" opacity="0.9" />
                {[0, 1, 2].map((m) => (
                  <rect key={m} x={112 + m * 44} y={y + 17} width="32" height="14" rx="4" fill={i % 2 === 0 ? "#EFF6FF" : "#F5F3FF"} stroke={stroke} strokeWidth="1" opacity="0.9" />
                ))}
                <circle cx="70" cy={y + 24} r="4.5" fill="#fff" stroke={stroke} strokeWidth="2" />
                <line x1="70" y1={y + 24} x2="90" y2={y + 24} stroke={stroke} strokeWidth="1.4" opacity="0.5" />
              </g>
            ))}
            <text x="404" y="70" fontSize="10" fill="#94A3B8" fontFamily="'Geist Mono', monospace" letterSpacing="1.5">api</text>
            <text x="404" y="140" fontSize="10" fill="#94A3B8" fontFamily="'Geist Mono', monospace" letterSpacing="1.5">app</text>
            <text x="404" y="210" fontSize="10" fill="#94A3B8" fontFamily="'Geist Mono', monospace" letterSpacing="1.5">core</text>
            <text x="404" y="280" fontSize="10" fill="#94A3B8" fontFamily="'Geist Mono', monospace" letterSpacing="1.5">db</text>
          </g>
        )}

        {variant === "web" && (
          <g>
            <rect x="50" y="56" width="290" height="190" rx="14" fill="#fff" stroke={stroke} strokeWidth="1.5" />
            <line x1="50" y1="88" x2="340" y2="88" stroke="#E2E8F0" strokeWidth="1.2" />
            {[64, 76, 88].map((cx, i) => (
              <circle key={i} cx={cx} cy="72" r="3.5" fill="#E2E8F0" />
            ))}
            <rect x="70" y="108" width="120" height="14" rx="4" fill="#EFF6FF" stroke={stroke} strokeWidth="1" />
            <rect x="70" y="134" width="250" height="8" rx="4" fill="#F1F5F9" />
            <rect x="70" y="150" width="210" height="8" rx="4" fill="#F1F5F9" />
            <rect x="70" y="176" width="80" height="26" rx="8" fill={stroke} opacity="0.9" />
            <rect x="330" y="110" width="100" height="170" rx="16" fill="#fff" stroke={stroke} strokeWidth="1.5" />
            <rect x="346" y="132" width="68" height="10" rx="4" fill="#F5F3FF" stroke={stroke} strokeWidth="1" />
            <rect x="346" y="152" width="68" height="6" rx="3" fill="#F1F5F9" />
            <rect x="346" y="164" width="52" height="6" rx="3" fill="#F1F5F9" />
            <rect x="346" y="184" width="68" height="56" rx="8" fill="#EFF6FF" stroke={stroke} strokeWidth="1" opacity="0.9" />
            <circle cx="380" cy="258" r="6" fill="none" stroke={stroke} strokeWidth="1.5" />
          </g>
        )}

        {variant === "ai" && (
          <g>
            {[[240, 70], [380, 120], [400, 240], [240, 270], [90, 230], [80, 110]].map(([x, y], i) => (
              <line key={i} x1="240" y1="170" x2={x} y2={y} stroke={stroke} strokeWidth="1.3" opacity="0.45" className={i % 2 === 0 ? "dash-flow" : ""} />
            ))}
            <circle cx="240" cy="170" r="12" fill="none" stroke={stroke} strokeWidth="1.5" className="node-pulse" />
            <circle cx="240" cy="170" r="9" fill={stroke} />
            {[[240, 70], [380, 120], [400, 240], [240, 270], [90, 230], [80, 110]].map(([x, y], i) => (
              <circle key={i} cx={x} cy={y} r="6" fill="#fff" stroke={stroke} strokeWidth="2" />
            ))}
            <text x="222" y="50" fontSize="10" fill="#94A3B8" fontFamily="'Geist Mono', monospace" letterSpacing="1.5">model</text>
            <text x="356" y="102" fontSize="10" fill="#94A3B8" fontFamily="'Geist Mono', monospace" letterSpacing="1.5">data</text>
          </g>
        )}

        {variant === "cloud" && (
          <g>
            <circle cx="200" cy="86" r="34" fill="#fff" stroke={stroke} strokeWidth="1.5" />
            <circle cx="252" cy="72" r="42" fill="#fff" stroke={stroke} strokeWidth="1.5" />
            <circle cx="298" cy="92" r="28" fill="#fff" stroke={stroke} strokeWidth="1.5" />
            <rect x="166" y="86" width="160" height="34" rx="17" fill="#fff" />
            <rect x="166" y="86" width="160" height="34" rx="17" fill="none" stroke={stroke} strokeWidth="0" />
            {[[150, 190, "app"], [220, 190, "db"], [290, 190, "cdn"]].map(([x, y, l]) => (
              <line key={l} x1={x + 20} y1="124" x2={x + 20} y2={y} stroke={stroke} strokeWidth="1.3" opacity="0.5" className="dash-flow" />
            ))}
            {[["app", 130], ["database", 210], ["infra", 290]].map(([l, x]) => (
              <g key={l}>
                <rect x={x} y="190" width="90" height="34" rx="10" fill="#fff" stroke={stroke} strokeWidth="1.4" />
                <text x={x + 45} y="211" fontSize="10" fill="#64748B" fontFamily="'Geist Mono', monospace" letterSpacing="1" textAnchor="middle">{l}</text>
              </g>
            ))}
            <rect x="130" y="248" width="250" height="34" rx="10" fill="#F5F3FF" stroke={stroke} strokeWidth="1.2" />
            <text x="255" y="269" fontSize="10" fill="#64748B" fontFamily="'Geist Mono', monospace" letterSpacing="1" textAnchor="middle">deploy pipeline</text>
          </g>
        )}

        {variant === "transform" && (
          <g>
            {[["manual processes", 34], ["integrated systems", 108], ["automated workflows", 182], ["modern operations", 256]].map(([l, y], i) => (
              <g key={l}>
                <rect x="130" y={y} width="220" height="46" rx="12" fill={i === 3 ? stroke : "#fff"} stroke={stroke} strokeWidth="1.4" opacity={i === 3 ? 0.95 : 1} />
                <text x="240" y={y + 28} fontSize="11" fill={i === 3 ? "#fff" : "#64748B"} fontFamily="'Geist Mono', monospace" letterSpacing="1" textAnchor="middle">{l}</text>
                {i < 3 && (
                  <g>
                    <line x1="240" y1={y + 46} x2="240" y2={y + 72} stroke={stroke} strokeWidth="1.4" opacity="0.6" className="dash-flow" />
                    <path d={`M236 ${y + 68} l4 8 4-8`} fill="none" stroke={stroke} strokeWidth="1.6" />
                  </g>
                )}
              </g>
            ))}
          </g>
        )}

        {variant === "consulting" && (
          <g>
            <line x1="240" y1="76" x2="150" y2="140" stroke={stroke} strokeWidth="1.4" opacity="0.5" />
            <line x1="240" y1="76" x2="330" y2="140" stroke={stroke} strokeWidth="1.4" opacity="0.5" />
            <line x1="150" y1="166" x2="100" y2="226" stroke={stroke} strokeWidth="1.2" opacity="0.4" />
            <line x1="150" y1="166" x2="200" y2="226" stroke={stroke} strokeWidth="1.2" opacity="0.4" />
            <line x1="330" y1="166" x2="280" y2="226" stroke={stroke} strokeWidth="1.2" opacity="0.4" />
            <line x1="330" y1="166" x2="380" y2="226" stroke={stroke} strokeWidth="1.2" opacity="0.4" />
            <circle cx="240" cy="62" r="12" fill={stroke} />
            <circle cx="240" cy="62" r="12" fill="none" stroke={stroke} strokeWidth="1.5" className="node-pulse" />
            <rect x="126" y="140" width="48" height="26" rx="8" fill="#fff" stroke={stroke} strokeWidth="1.5" />
            <rect x="306" y="140" width="48" height="26" rx="8" fill="#fff" stroke={stroke} strokeWidth="1.5" />
            {[[100, 226], [200, 226], [280, 226], [380, 226]].map(([x, y]) => (
              <rect key={x} x={x - 16} y={y - 12} width="32" height="24" rx="7" fill="#F5F3FF" stroke={stroke} strokeWidth="1.2" />
            ))}
            <text x="240" y="40" fontSize="10" fill="#94A3B8" fontFamily="'Geist Mono', monospace" letterSpacing="1.5" textAnchor="middle">strategy</text>
          </g>
        )}
      </svg>
    </div>
  );
}

export default function ServicesPage() {
  useHashScroll();

  return (
    <main className="bg-white" data-testid="services-page">
      {/* Hero */}
      <section className="relative overflow-hidden pt-[72px]">
        <div className="bg-grid-light absolute inset-0" aria-hidden="true" />
        <div className="absolute -left-40 top-[-120px] h-[420px] w-[420px] rounded-full bg-blue-600/10 blur-[120px]" aria-hidden="true" />
        <div className="relative mx-auto grid w-full max-w-[1200px] items-center gap-14 px-6 py-20 md:px-8 md:py-28 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE }}>
              <Eyebrow>Our services</Eyebrow>
            </motion.div>
            <motion.h1
              data-testid="services-hero-heading"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.95, ease: EASE, delay: 0.12 }}
              className="mt-6 text-[clamp(2.5rem,5vw,4.2rem)] font-extrabold leading-[1.03] tracking-[-0.03em] text-slate-900"
            >
              Technology services designed to{" "}
              <span className="bg-gradient-to-r from-blue-600 to-violet-600 bg-clip-text text-transparent">
                build, modernize, and scale.
              </span>
            </motion.h1>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: EASE, delay: 0.28 }}
            >
              <p className="mt-6 max-w-[540px] text-base leading-relaxed text-slate-600 md:text-lg">
                Bytevora combines software engineering, product development, cloud, AI, automation,
                and technology consulting to help businesses turn ideas and operational challenges
                into reliable digital solutions.
              </p>
              <div className="mt-9 flex flex-wrap items-center gap-4">
                <Link
                  to="/contact"
                  data-testid="services-start-project-button"
                  className="group inline-flex items-center gap-2.5 rounded-[10px] bg-navy px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-blue-600"
                >
                  Start a Project
                  <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
                <Link
                  to="/contact"
                  data-testid="services-talk-to-us-link"
                  className="group inline-flex items-center gap-2 px-2 py-3.5 text-sm font-semibold text-slate-900 transition-colors duration-300 hover:text-blue-600"
                >
                  Talk to Us
                  <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>
            </motion.div>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, ease: EASE, delay: 0.35 }}
            className="hidden lg:col-span-5 lg:block"
          >
            <ServiceVisual variant="software" />
          </motion.div>
        </div>
      </section>

      {/* Overview */}
      <section className="bg-[#F8FAFC] py-24 md:py-28" data-testid="services-overview">
        <div className="mx-auto w-full max-w-[1200px] px-6 md:px-8">
          <Reveal className="max-w-2xl">
            <h2 className="text-3xl font-bold leading-[1.08] tracking-[-0.02em] text-slate-900 md:text-[2.5rem]">
              From product ideas to production systems.
            </h2>
            <p className="mt-5 text-[15px] leading-[1.8] text-slate-600 md:text-base">
              Our capabilities cover the full lifecycle of modern digital products — from strategy
              and architecture to engineering, infrastructure, automation, integration, and
              ongoing technical support.
            </p>
          </Reveal>
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-3"
          >
            {detailServices.map((s, i) => (
              <motion.button
                key={s.slug}
                type="button"
                variants={fadeUp}
                data-testid={`services-overview-${s.slug}`}
                onClick={() => scrollToHash(`#${s.slug}`)}
                className="group flex h-full flex-col rounded-[18px] border border-slate-200 bg-white p-7 text-left transition-[transform,border-color,box-shadow] duration-300 hover:-translate-y-1 hover:border-blue-600/40 hover:shadow-[0_28px_55px_-32px_rgba(37,99,235,0.4)]"
              >
                <div className="flex items-start justify-between">
                  <span className="grid h-11 w-11 place-items-center rounded-xl border border-slate-200 bg-slate-50 text-blue-600">
                    <s.icon size={19} strokeWidth={1.7} />
                  </span>
                  <span className="font-mono text-xs tracking-[0.18em] text-slate-300 transition-colors duration-300 group-hover:text-blue-600">
                    0{i + 1}
                  </span>
                </div>
                <h3 className="mt-6 text-lg font-semibold tracking-tight text-slate-900">{s.title}</h3>
                <p className="mt-2.5 flex-1 text-sm leading-relaxed text-slate-600">{s.overviewDesc}</p>
                <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-900 transition-colors duration-300 group-hover:text-blue-600">
                  Explore Service
                  <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </motion.button>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Detailed blocks */}
      {detailServices.map((s, i) => (
        <section
          key={s.slug}
          id={s.slug}
          data-testid={`service-detail-${s.slug}`}
          className={`scroll-mt-28 py-24 md:py-28 ${i % 2 === 0 ? "bg-white" : "bg-[#F8FAFC]"}`}
        >
          <div className="mx-auto grid w-full max-w-[1200px] items-center gap-14 px-6 md:px-8 lg:grid-cols-2">
            <Reveal className={i % 2 === 1 ? "lg:order-2" : ""}>
              <div className="flex items-center gap-4">
                <span className="grid h-12 w-12 place-items-center rounded-xl border border-slate-200 bg-white text-blue-600 shadow-sm">
                  <s.icon size={20} strokeWidth={1.7} />
                </span>
                <span className="font-mono text-xs tracking-[0.2em] text-slate-400">{s.num} — SERVICE</span>
              </div>
              <h2 className="mt-7 text-3xl font-bold leading-[1.12] tracking-[-0.02em] text-slate-900 md:text-[2.3rem]">
                {s.heading}
              </h2>
              <p className="mt-5 max-w-lg text-[15px] leading-[1.8] text-slate-600">{s.copy}</p>
              <div className="mt-8 grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
                {s.capabilities.map((c) => (
                  <span key={c} className="flex items-center gap-2.5 text-sm text-slate-700">
                    <Check size={14} className="shrink-0 text-blue-600" strokeWidth={2.5} />
                    {c}
                  </span>
                ))}
              </div>
              <Link
                to="/contact"
                data-testid={`service-detail-cta-${s.slug}`}
                className="group mt-9 inline-flex items-center gap-2 text-sm font-semibold text-slate-900 transition-colors duration-300 hover:text-blue-600"
              >
                Discuss {s.short}
                <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Reveal>
            <Reveal delay={1} className={i % 2 === 1 ? "lg:order-1" : ""}>
              <ServiceVisual variant={s.variant} />
            </Reveal>
          </div>
        </section>
      ))}

      {/* Approach */}
      <section className="bg-white py-24 md:py-28" data-testid="services-approach">
        <div className="mx-auto grid w-full max-w-[1200px] gap-14 px-6 md:px-8 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-28">
              <Reveal>
                <Eyebrow>Our approach</Eyebrow>
                <h2 className="mt-5 text-3xl font-bold leading-[1.08] tracking-[-0.02em] text-slate-900 md:text-[2.4rem]">
                  Technology decisions should follow the problem.
                </h2>
                <p className="mt-5 max-w-md text-sm leading-relaxed text-slate-600 md:text-base">
                  We begin with the business requirement, user experience, technical constraints,
                  and long-term goals before deciding how a solution should be designed or which
                  technologies should power it.
                </p>
              </Reveal>
            </div>
          </div>
          <div className="lg:col-span-7">
            {approach.map((p, i) => (
              <Reveal key={p.title} delay={i} className={`group grid grid-cols-[64px_1fr] gap-6 border-t border-slate-200 py-8 md:grid-cols-[88px_1fr_1.1fr] md:px-4 ${i === approach.length - 1 ? "border-b" : ""}`}>
                <span className="pt-1 font-mono text-sm tracking-[0.16em] text-slate-300 transition-colors duration-300 group-hover:text-blue-600">
                  /0{i + 1}
                </span>
                <h3 className="text-lg font-semibold tracking-tight text-slate-900">{p.title}</h3>
                <p className="col-span-2 pl-[88px] text-sm leading-relaxed text-slate-600 md:col-span-1 md:pl-0 md:pt-0.5">
                  {p.desc}
                </p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Cross-service */}
      <section className="bg-[linear-gradient(180deg,#F8FAFC_0%,#EEF3FF_100%)] py-24 md:py-28" data-testid="services-cross-service">
        <div className="mx-auto w-full max-w-[1200px] px-6 text-center md:px-8">
          <Reveal className="flex justify-center">
            <Eyebrow>Cross-service capability</Eyebrow>
          </Reveal>
          <Reveal delay={1}>
            <h2 className="mx-auto mt-5 max-w-2xl text-3xl font-bold leading-[1.08] tracking-[-0.02em] text-slate-900 md:text-[2.4rem]">
              One project rarely fits inside one category.
            </h2>
            <p className="mx-auto mt-5 max-w-xl text-[15px] leading-[1.8] text-slate-600">
              A SaaS platform may require product engineering, cloud infrastructure, APIs,
              automation, mobile experiences, and ongoing technical consulting. Bytevora&rsquo;s
              service model is designed to work across those boundaries.
            </p>
          </Reveal>
          <Reveal delay={2} className="mt-12 flex flex-col items-center justify-center gap-3 md:flex-row md:gap-0">
            {["Product Engineering", "APIs & Integrations", "Cloud Infrastructure", "AI & Automation", "Support & Optimization"].map((step, i) => (
              <div key={step} className="flex flex-col items-center md:flex-row">
                <span className="whitespace-nowrap rounded-full border border-slate-200 bg-white px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.16em] text-slate-700 shadow-sm">
                  {step}
                </span>
                {i < 4 && (
                  <>
                    <ArrowRight size={16} className="mx-3 hidden text-slate-400 md:block" aria-hidden="true" />
                    <ArrowDown size={16} className="my-1 text-slate-400 md:hidden" aria-hidden="true" />
                  </>
                )}
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      {/* Who We Help */}
      <section className="bg-white py-24 md:py-28" data-testid="services-who-we-help">
        <div className="mx-auto w-full max-w-[1200px] px-6 md:px-8">
          <Reveal className="max-w-xl">
            <Eyebrow>Who we help</Eyebrow>
            <h2 className="mt-5 text-3xl font-bold leading-[1.08] tracking-[-0.02em] text-slate-900 md:text-[2.4rem]">
              Technology support for businesses at different stages.
            </h2>
          </Reveal>
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4"
          >
            {audiences.map((a, i) => (
              <motion.div
                key={a.title}
                variants={fadeUp}
                data-testid={`audience-${i}`}
                className="group rounded-[18px] border border-slate-200 bg-white p-7 transition-[transform,border-color] duration-300 hover:-translate-y-1 hover:border-blue-600/35"
              >
                <span className="font-mono text-xs tracking-[0.18em] text-slate-300 transition-colors duration-300 group-hover:text-blue-600">
                  0{i + 1}
                </span>
                <h3 className="mt-4 text-[17px] font-semibold tracking-tight text-slate-900">{a.title}</h3>
                <p className="mt-2.5 text-sm leading-relaxed text-slate-600">{a.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Why Bytevora (short) */}
      <section className="bg-[#F8FAFC] py-24 md:py-28" data-testid="services-why">
        <div className="mx-auto grid w-full max-w-[1200px] gap-12 px-6 md:px-8 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <Eyebrow>Why Bytevora</Eyebrow>
            <h2 className="mt-5 text-3xl font-bold leading-[1.08] tracking-[-0.02em] text-slate-900 md:text-[2.4rem]">
              Built around engineering, not just delivery.
            </h2>
          </Reveal>
          <div className="grid gap-8 sm:grid-cols-2 lg:col-span-7">
            {whyPoints.map((p, i) => (
              <Reveal key={p.title} delay={i}>
                <span className="grid h-8 w-8 place-items-center rounded-full bg-blue-600/10 text-blue-600">
                  <Check size={15} strokeWidth={2.5} />
                </span>
                <h3 className="mt-4 text-[16px] font-semibold tracking-tight text-slate-900">{p.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{p.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative overflow-hidden bg-navy py-24 md:py-32" data-testid="services-cta">
        <div className="bg-grid-dark absolute inset-0" aria-hidden="true" />
        <div className="absolute -left-40 top-[-120px] h-[420px] w-[420px] rounded-full bg-blue-600/25 blur-[130px]" aria-hidden="true" />
        <div className="relative mx-auto max-w-[860px] px-6 text-center md:px-8">
          <Reveal>
            <h2 className="text-4xl font-extrabold leading-[1.05] tracking-[-0.03em] text-white sm:text-5xl">
              Not sure which service{" "}
              <span className="bg-gradient-to-r from-blue-400 to-violet-400 bg-clip-text text-transparent">
                fits your project?
              </span>
            </h2>
          </Reveal>
          <Reveal delay={1}>
            <p className="mx-auto mt-6 max-w-xl text-sm leading-relaxed text-slate-400 md:text-base">
              Tell us what you&rsquo;re trying to build, improve, or automate. We&rsquo;ll help
              identify the right technical direction.
            </p>
          </Reveal>
          <Reveal delay={2} className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/contact"
              data-testid="services-cta-start-project"
              className="group inline-flex items-center gap-2.5 rounded-[10px] bg-white px-7 py-3.5 text-sm font-semibold text-navy transition-colors duration-300 hover:bg-blue-600 hover:text-white"
            >
              Start a Project
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/contact"
              data-testid="services-cta-contact"
              className="group inline-flex items-center gap-2 px-2 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:text-blue-300"
            >
              Contact Bytevora
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
